import { decodeText } from './bytes-text.js';
import { CsvRefusedError, readCsvRows } from './csv-read.js';
import type { Currency } from './currency.js';
import { readCamt053, StatementFormatError } from './statement-camt053.js';
import { readMt940 } from './statement-mt940.js';
import {
  balanceGaps,
  columnIndex,
  dedupeKeys,
  type ParsedStatementLine,
  parseStatementRow,
  type StatementFileRow,
  type StatementFormat,
  type StatementMapping,
  type StatementRead,
  type StatementRowIssue,
  statementMappingSchema,
} from './statement-profile.js';
import { lowerTurkish } from './text.js';
import {
  compoundFileHoldsWorkbook,
  isCompoundFile,
  readXlsFirstSheet,
  XlsRefusedError,
} from './xls-read.js';
import { isXlsxRefusal, readXlsxFirstSheet } from './xlsx-read.js';
import { XmlRefusedError } from './xml-scan.js';
import type { InflateRaw } from './zip-read.js';

/** The `statement_import` upload kind's size limit, enforced before a byte is read. */
export const STATEMENT_FILE_MAX_BYTES = 10 * 1024 * 1024;

/** A month of a busy account is a few thousand lines; a file beyond this is refused whole. */
export const STATEMENT_MAX_ROWS = 20_000;

/**
 * The text a mapped file's staging rows may hold together, each row's header
 * names counted again because each row's `raw_row` carries them. Without it a
 * header row of long cells, or one long shared string named by every cell,
 * reads as gigabytes of `raw_row` and the import cannot be written.
 */
export const STATEMENT_MAX_RAW_CHARS = 32_000_000;

const UNPAIRED_SURROGATE =
  /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;

/**
 * Text Postgres will store: a NUL and an unpaired surrogate — which a `text`
 * column and `jsonb` both refuse — become U+FFFD, so a file carrying one is
 * read and shown rather than failing when its rows are written.
 */
export function storableText(text: string): string {
  return text.replaceAll('\u0000', '\uFFFD').replace(UNPAIRED_SURROGATE, '\uFFFD');
}

export const STATEMENT_FAILURE_CODES = [
  'STATEMENT_UNREADABLE',
  'STATEMENT_PROFILE_MISMATCH',
  'STATEMENT_ACCOUNT_MISMATCH',
  'STATEMENT_ACCOUNT_IBAN_MISSING',
  'STATEMENT_TOO_MANY_ROWS',
  'STATEMENT_EMPTY',
  'STATEMENT_FILE_INFECTED',
  'STATEMENT_FILE_SCAN_FAILED',
  'STATEMENT_FILE_CHANGED',
  'STATEMENT_FILE_ENCRYPTED',
  'STATEMENT_FILE_TOO_COMPLEX',
  'REQUESTER_NOT_FOUND',
  'REQUESTER_INACTIVE',
  'REQUESTER_LACKS_PERMISSION',
  'REQUESTER_OUT_OF_SCOPE',
] as const;
export type StatementFailureCode = (typeof STATEMENT_FAILURE_CODES)[number];

export class StatementFileError extends Error {
  readonly code: StatementFailureCode;

  constructor(code: StatementFailureCode, message: string) {
    super(message);
    this.name = 'StatementFileError';
    this.code = code;
  }
}

export const STATEMENT_FILE_KINDS = ['xlsx', 'xls', 'xml', 'mt940', 'csv', 'unknown'] as const;
export type StatementFileKind = (typeof STATEMENT_FILE_KINDS)[number];

/** The content type a sniffed kind is stored as — decided by the bytes, never by the browser. */
export const STATEMENT_FILE_CONTENT_TYPES: Record<StatementFileKind, string> = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  xml: 'application/xml',
  mt940: 'text/plain',
  csv: 'text/csv',
  unknown: 'application/octet-stream',
};

/**
 * Which files a profile of each format reads. A mapped spreadsheet profile
 * describes a bank's columns, not its container, so it reads the export as an
 * `.xlsx` or as a legacy `.xls` alike — docs/decisions/0101.
 */
export const STATEMENT_FORMAT_FILE_KINDS: Record<StatementFormat, readonly StatementFileKind[]> = {
  xlsx_mapped: ['xlsx', 'xls'],
  csv_mapped: ['csv'],
  camt053: ['xml'],
  mt940: ['mt940'],
};

export function formatReadsFileKind(format: StatementFormat, kind: StatementFileKind): boolean {
  return STATEMENT_FORMAT_FILE_KINDS[format].includes(kind);
}

const SNIFF_BYTES = 64 * 1024;

function startsWith(bytes: Uint8Array, signature: readonly number[]): boolean {
  return signature.every((byte, index) => bytes[index] === byte);
}

function containsAscii(bytes: Uint8Array, text: string): boolean {
  const needle = Array.from(text, (char) => char.charCodeAt(0));
  outer: for (let index = 0; index + needle.length <= bytes.length; index += 1) {
    for (let offset = 0; offset < needle.length; offset += 1) {
      if (bytes[index + offset] !== needle[offset]) continue outer;
    }
    return true;
  }
  return false;
}

/**
 * What a file **is**, from its bytes. An `.xlsx` is a ZIP whose first entries
 * name the spreadsheet parts; the legacy binary `.xls` is an OLE compound file
 * whose root holds a workbook — or the encrypted package a password wraps an
 * `.xlsx` in, which the reader then refuses by name; XML, MT940 and delimited text
 * are told apart by their first characters once decoded. A file that holds a
 * NUL outside those containers is binary and `unknown`.
 */
export function sniffStatementFile(bytes: Uint8Array): StatementFileKind {
  const head = bytes.subarray(0, SNIFF_BYTES);
  if (startsWith(head, [0x50, 0x4b, 0x03, 0x04])) {
    return containsAscii(
      bytes.subarray(0, Math.min(bytes.length, 1024 * 1024)),
      'xl/workbook.xml',
    ) || containsAscii(head, '[Content_Types].xml')
      ? 'xlsx'
      : 'unknown';
  }
  if (isCompoundFile(head)) return compoundFileHoldsWorkbook(bytes) ? 'xls' : 'unknown';

  const isUtf16 = startsWith(head, [0xff, 0xfe]) || startsWith(head, [0xfe, 0xff]);
  if (!isUtf16 && head.includes(0)) return 'unknown';

  const text = decodeText(head).text.replace(/^﻿/, '').trimStart();
  if (text.startsWith('<?xml') || text.startsWith('<Document')) return 'xml';
  if (/^(\{1:|:20:)/.test(text) && /\n:(60F|60M|61):/.test(text.replace(/\r/g, ''))) {
    return 'mt940';
  }
  if (text.startsWith('<')) return 'unknown';
  return text.length > 0 ? 'csv' : 'unknown';
}

export type StatementFileInput = {
  readonly format: StatementFormat;
  readonly mapping: unknown;
  readonly bytes: Uint8Array;
  readonly accountCurrency: Currency;
  readonly inflateRaw: InflateRaw;
  readonly maxRows?: number;
};

function normalizeHeader(text: string): string {
  return lowerTurkish(text).replace(/\s+/g, ' ').trim();
}

function columnLetter(index: number): string {
  let letters = '';
  let rest = index + 1;
  while (rest > 0) {
    const remainder = (rest - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    rest = Math.floor((rest - 1) / 26);
  }
  return letters;
}

/** The mapped formats' rows, read by a profile's column map. */
export function readMappedRows(
  mapping: StatementMapping,
  grid: readonly (readonly string[])[],
  accountCurrency: Currency,
  maxRawChars: number = STATEMENT_MAX_RAW_CHARS,
): StatementRead {
  const rows = grid.map((cells) => cells.map(storableText));
  const header = rows[mapping.headerRow - 1] ?? [];
  for (const [column, expected] of Object.entries(mapping.expectedHeaders ?? {})) {
    const actual = header[columnIndex(column)] ?? '';
    if (normalizeHeader(actual) !== normalizeHeader(expected)) {
      throw new StatementFileError(
        'STATEMENT_PROFILE_MISMATCH',
        `Column ${column} is not headed as the profile expects`,
      );
    }
  }
  const headers: Record<string, string> = {};
  let headerChars = 0;
  header.forEach((text, index) => {
    if (text.trim() !== '') {
      headers[columnLetter(index)] = text.trim();
      headerChars += text.trim().length;
    }
  });
  let rawChars = 0;

  const read: StatementFileRow[] = [];
  for (let index = mapping.firstDataRow - 1; index < rows.length; index += 1) {
    const cells = rows[index] ?? [];
    const parsed = parseStatementRow(mapping, cells, accountCurrency);
    if (parsed.issues.includes('ROW_EMPTY')) continue;
    const values: Record<string, string> = {};
    rawChars += headerChars;
    cells.forEach((text, column) => {
      if (text !== '') {
        values[columnLetter(column)] = text;
        rawChars += text.length;
      }
    });
    if (rawChars > maxRawChars) {
      throw new StatementFileError(
        'STATEMENT_FILE_TOO_COMPLEX',
        'The rows hold more text than an import may keep',
      );
    }
    read.push({
      rowNumber: index + 1,
      rawRow: { source: 'sheet', row: index + 1, cells: values, headers },
      line: parsed.line,
      issues: parsed.issues,
    });
  }

  if (read.length > 0 && read.every((row) => row.line === null)) {
    throw new StatementFileError(
      'STATEMENT_PROFILE_MISMATCH',
      'Not one row of the file reads as a statement line under this profile',
    );
  }

  const withLines = read.filter((row) => row.line !== null);
  const gapRows = new Set(
    balanceGaps(withLines.map((row) => row.line as ParsedStatementLine)).map(
      (gap) => withLines[gap.index],
    ),
  );
  return {
    accountIdentifiers: [],
    rows: read.map((row) =>
      gapRows.has(row) ? { ...row, issues: [...row.issues, 'BALANCE_GAP'] } : row,
    ),
  };
}

/**
 * The failure code a bounded reader's own refusal reaches a person as, or null
 * for an exception no reader names. The `CSV_*`, `XLS_*`, `XLSX_*`, `XML_*` and
 * `ZIP_*` codes are in no translated list and never leave `packages/core`.
 */
export function statementFailureOf(error: unknown): StatementFileError | null {
  if (error instanceof StatementFileError) return error;
  if (error instanceof StatementFormatError)
    return new StatementFileError(error.code, error.message);
  if (error instanceof XlsRefusedError) {
    const code =
      error.code === 'XLS_ENCRYPTED'
        ? 'STATEMENT_FILE_ENCRYPTED'
        : error.code === 'XLS_TOO_MANY_ROWS'
          ? 'STATEMENT_TOO_MANY_ROWS'
          : error.code === 'XLS_TOO_MUCH_TEXT'
            ? 'STATEMENT_FILE_TOO_COMPLEX'
            : 'STATEMENT_UNREADABLE';
    return new StatementFileError(code, error.message);
  }
  if (isXlsxRefusal(error)) {
    const code =
      error.code === 'XLSX_TOO_MANY_ROWS'
        ? 'STATEMENT_TOO_MANY_ROWS'
        : error.code === 'XLSX_TOO_MUCH_TEXT'
          ? 'STATEMENT_FILE_TOO_COMPLEX'
          : 'STATEMENT_UNREADABLE';
    return new StatementFileError(code, error.message);
  }
  if (error instanceof CsvRefusedError) {
    return new StatementFileError(
      error.code === 'CSV_TOO_MANY_ROWS' ? 'STATEMENT_TOO_MANY_ROWS' : 'STATEMENT_UNREADABLE',
      error.message,
    );
  }
  if (error instanceof XmlRefusedError) {
    return new StatementFileError('STATEMENT_UNREADABLE', error.message);
  }
  return null;
}

function translate(error: unknown): never {
  throw statementFailureOf(error) ?? error;
}

export type StatementGridInput = {
  readonly kind: StatementFileKind;
  readonly bytes: Uint8Array;
  readonly decimalSeparator: ',' | '.';
  readonly csvDelimiter?: StatementMapping['csvDelimiter'];
  readonly inflateRaw: InflateRaw;
  readonly maxRows: number;
};

/**
 * A spreadsheet or delimited file as rows of the text a person sees — what a
 * mapped profile's columns are chosen against, and what `readStatementFile`
 * maps. One function, so the editor's sample and the import read alike.
 */
export async function readStatementGrid(input: StatementGridInput): Promise<string[][]> {
  try {
    switch (input.kind) {
      case 'xlsx':
        return (
          await readXlsxFirstSheet(input.bytes, {
            inflateRaw: input.inflateRaw,
            decimalSeparator: input.decimalSeparator,
            maxRows: input.maxRows,
          })
        ).rows;
      case 'xls':
        return readXlsFirstSheet(input.bytes, {
          decimalSeparator: input.decimalSeparator,
          maxRows: input.maxRows,
        }).rows;
      case 'csv':
        return readCsvRows(decodeText(input.bytes).text, {
          delimiter: input.csvDelimiter,
          maxRows: input.maxRows,
          maxColumns: 702,
        });
      default:
        throw new StatementFileError(
          'STATEMENT_PROFILE_MISMATCH',
          `A ${input.kind} file is not read through a column map`,
        );
    }
  } catch (error) {
    translate(error);
  }
}

/**
 * A statement file, read the way its profile says. Throws `StatementFileError`
 * with a failure code for a file that cannot be read at all; a row that cannot
 * be read is kept, with its issues, for the preview.
 */
export async function readStatementFile(input: StatementFileInput): Promise<StatementRead> {
  const maxRows = input.maxRows ?? STATEMENT_MAX_ROWS;
  const kind = sniffStatementFile(input.bytes);
  if (!formatReadsFileKind(input.format, kind)) {
    throw new StatementFileError(
      kind === 'unknown' ? 'STATEMENT_UNREADABLE' : 'STATEMENT_PROFILE_MISMATCH',
      `The file is ${kind}, and a ${input.format} profile reads ${STATEMENT_FORMAT_FILE_KINDS[input.format].join(' or ')}`,
    );
  }

  try {
    let read: StatementRead;
    if (input.format === 'camt053') {
      read = readCamt053(storableText(decodeText(input.bytes).text), maxRows);
    } else if (input.format === 'mt940') {
      read = readMt940(storableText(decodeText(input.bytes).text), maxRows);
    } else {
      const parsedMapping = statementMappingSchema.safeParse(input.mapping);
      if (!parsedMapping.success) {
        throw new StatementFileError(
          'STATEMENT_PROFILE_MISMATCH',
          'The profile has no valid mapping',
        );
      }
      const mapping = parsedMapping.data;
      const rowLimit = maxRows + mapping.firstDataRow;
      const rows = await readStatementGrid({
        kind,
        bytes: input.bytes,
        decimalSeparator: mapping.decimalSeparator,
        csvDelimiter: mapping.csvDelimiter,
        inflateRaw: input.inflateRaw,
        maxRows: rowLimit,
      });
      read = readMappedRows(mapping, rows, input.accountCurrency);
    }
    if (read.rows.length > maxRows) {
      throw new StatementFileError('STATEMENT_TOO_MANY_ROWS', 'The file has too many rows');
    }
    if (read.rows.length === 0) {
      throw new StatementFileError('STATEMENT_EMPTY', 'The file holds no statement line');
    }
    return read;
  } catch (error) {
    translate(error);
  }
}

export function normalizeAccountIdentifier(value: string): string {
  return value.replace(/[\s-]/g, '').toUpperCase();
}

/**
 * Whether a file that names its account names this one. A file that names none
 * — every mapped export — cannot be checked and passes. One that does must name
 * the account's IBAN or its bank reference exactly, or, in MT940's
 * `bank code/account number` form, a bank code and account number the IBAN is
 * built from. An account with neither on record fails: a statement that says
 * which account it belongs to is never committed to an account that cannot
 * answer.
 */
export function statementNamesAccount(
  identifiers: readonly string[],
  account: { iban: string | null; externalRef: string | null },
): boolean {
  if (identifiers.length === 0) return true;
  const iban = account.iban === null ? null : normalizeAccountIdentifier(account.iban);
  const reference =
    account.externalRef === null ? null : normalizeAccountIdentifier(account.externalRef);
  return identifiers.every((identifier) => {
    const normalized = normalizeAccountIdentifier(identifier);
    if (normalized === '') return false;
    if (normalized === iban || normalized === reference) return true;
    const national = /^(\d{8})\/(\d{1,10})$/.exec(normalized);
    if (iban === null || national === null) return false;
    const [, bankCode = '', accountNumber = ''] = national;
    return iban.slice(4) === `${bankCode}${accountNumber.padStart(10, '0')}`;
  });
}

/**
 * Why a file that names its account may not be read into this one, or null
 * when it may. An account with no IBAN on record is told apart from one whose
 * IBAN the file contradicts: the first is fixed by entering the account's IBAN,
 * the second by uploading to the right account.
 */
export function statementAccountRefusal(
  identifiers: readonly string[],
  account: { iban: string | null; externalRef: string | null },
): 'STATEMENT_ACCOUNT_IBAN_MISSING' | 'STATEMENT_ACCOUNT_MISMATCH' | null {
  if (statementNamesAccount(identifiers, account)) return null;
  return account.iban === null ? 'STATEMENT_ACCOUNT_IBAN_MISSING' : 'STATEMENT_ACCOUNT_MISMATCH';
}

export const STATEMENT_ROW_OUTCOMES = ['valid', 'duplicate', 'rejected'] as const;
export type StatementRowOutcome = (typeof STATEMENT_ROW_OUTCOMES)[number];

export type AssessedStatementRow = StatementFileRow & {
  readonly dedupeKey: string | null;
  readonly outcome: StatementRowOutcome;
};

export type StatementAssessment = {
  readonly rows: readonly AssessedStatementRow[];
  readonly periodStart: string | null;
  readonly periodEnd: string | null;
};

/**
 * What the file says about itself before the database is asked: each line's
 * dedupe key with its in-file occurrence index, a line in a currency other
 * than the account's refused, and a reference repeated inside the one file
 * kept once. Whether a key is already committed on the account is the
 * server's question, asked afterwards.
 */
export function assessStatementRows(
  rows: readonly StatementFileRow[],
  accountCurrency: Currency,
): StatementAssessment {
  const readable = rows.filter((row) => row.line !== null);
  const keys = dedupeKeys(readable.map((row) => row.line as ParsedStatementLine));
  const keyByRow = new Map(readable.map((row, index) => [row, keys[index] as string]));
  const seen = new Set<string>();
  let periodStart: string | null = null;
  let periodEnd: string | null = null;

  const assessed = rows.map((row): AssessedStatementRow => {
    const line = row.line;
    if (line === null) return { ...row, dedupeKey: null, outcome: 'rejected' };
    const issues: StatementRowIssue[] = [...row.issues];
    const dedupeKey = keyByRow.get(row) ?? null;
    if (line.amount.currency !== accountCurrency) {
      issues.push('CURRENCY_NOT_ACCOUNT');
      return { ...row, issues, dedupeKey, outcome: 'rejected' };
    }
    if (periodStart === null || line.bookedOn < periodStart) periodStart = line.bookedOn;
    if (periodEnd === null || line.bookedOn > periodEnd) periodEnd = line.bookedOn;
    if (dedupeKey !== null && seen.has(dedupeKey)) {
      issues.push('DUPLICATE_IN_FILE');
      return { ...row, issues, dedupeKey, outcome: 'duplicate' };
    }
    if (dedupeKey !== null) seen.add(dedupeKey);
    return { ...row, issues, dedupeKey, outcome: 'valid' };
  });
  return { rows: assessed, periodStart, periodEnd };
}

export type StatementSamplePreview = {
  readonly kind: StatementFileKind;
  /** The file's first rows as text, for choosing columns; empty for CAMT.053 and MT940. */
  readonly grid: readonly (readonly string[])[];
  readonly assessment: StatementAssessment | null;
  readonly failure: { readonly code: StatementFailureCode; readonly message: string } | null;
};

/** How many of a sample's rows the editor shows as a grid. */
export const STATEMENT_SAMPLE_GRID_ROWS = 30;

/**
 * A sample file read under a draft profile, exactly as an import would read it
 * — `readStatementFile`, then `assessStatementRows` — beside its first rows as
 * plain text. The profile editor calls this in the browser, so the preview and
 * the import cannot disagree (docs/decisions/0047). Whether a line was already
 * committed is the server's question and is not asked here.
 */
export async function previewStatementSample(
  input: StatementFileInput,
): Promise<StatementSamplePreview> {
  const kind = sniffStatementFile(input.bytes);
  const draft = statementMappingSchema.safeParse(input.mapping);
  let grid: string[][] = [];
  if (kind === 'xlsx' || kind === 'xls' || kind === 'csv') {
    try {
      grid = (
        await readStatementGrid({
          kind,
          bytes: input.bytes,
          decimalSeparator: draft.success ? draft.data.decimalSeparator : ',',
          csvDelimiter: draft.success ? draft.data.csvDelimiter : undefined,
          inflateRaw: input.inflateRaw,
          maxRows: (input.maxRows ?? STATEMENT_MAX_ROWS) + STATEMENT_SAMPLE_GRID_ROWS,
        })
      ).slice(0, STATEMENT_SAMPLE_GRID_ROWS);
    } catch (error) {
      if (statementFailureOf(error) === null) throw error;
    }
  }
  try {
    const read = await readStatementFile(input);
    return {
      kind,
      grid,
      assessment: assessStatementRows(read.rows, input.accountCurrency),
      failure: null,
    };
  } catch (error) {
    const failure = statementFailureOf(error);
    if (failure === null) throw error;
    return {
      kind,
      grid,
      assessment: null,
      failure: { code: failure.code, message: failure.message },
    };
  }
}
